import React, { useContext, useEffect, useState, useRef } from "react";
import "./accountNotifications.css";
import translations from "../../../translations/translations";
import { CombinedContext } from "../../../context/combinedContext";
import usersRequest from "../../../request/usersRequest";
import AccountNotificationsSkeleton from "./accountNotificationsSkeleton/accountNotificationsSkeleton";

const AccountNotifications = () => {
    const { language, userData } = useContext(CombinedContext);
    const role = userData?.user?.role;
    const accessToken = userData?.cognitoTokens?.AccessToken;

    const [notificationPrefs, setNotificationPrefs] = useState(null);
    const [loading, setLoading] = useState(true);

    const saveTimeoutRef = useRef(null);
    const pendingUpdatesRef = useRef({});

    /* ----------------------------
       LOAD PREFS
    ----------------------------- */
    useEffect(() => {
        if (!accessToken) return;

        const loadPrefs = async () => {
            try {
                const resp = await usersRequest.getNotificationPreferences(accessToken);
                setNotificationPrefs(resp.notificationPrefs || {});
            } catch (err) {
                console.error("Failed to load notification prefs", err);
            } finally {
                setLoading(false);
            }
        };

        loadPrefs();
    }, [accessToken]);

    /* ----------------------------
       SAVE PREFS (DEBOUNCED)
    ----------------------------- */
    const savePrefs = (updates) => {
        // merge updates instead of replacing
        pendingUpdatesRef.current = {
            ...pendingUpdatesRef.current,
            ...updates,
        };

        if (saveTimeoutRef.current) return; // already scheduled

        saveTimeoutRef.current = setTimeout(async () => {
            const updatesToSend = pendingUpdatesRef.current;

            // reset BEFORE request to avoid race issues
            pendingUpdatesRef.current = {};
            saveTimeoutRef.current = null;

            try {
                await usersRequest.updateNotificationPreferences(updatesToSend, accessToken);
            } catch (err) {
                console.error("Failed to update notification prefs", err);

                // optional: rollback or retry
            }
        }, 400);
    };

    /* ----------------------------
       TOGGLE
    ----------------------------- */
    const toggleChannel = (key, channel) => {
        setNotificationPrefs((prev) => {
            const next = {
                ...prev,
                [key]: {
                    ...prev[key],
                    [channel]: !prev[key]?.[channel],
                },
            };

            savePrefs({ [key]: next[key] });
            return next;
        });
    };

    if (!role || loading || !notificationPrefs) {
        return <AccountNotificationsSkeleton />;
    }

    const getSection = (key) => {
        if (key.toLowerCase().includes("reminder")) return "reminders";
        if (key.toLowerCase().includes("review")) return "reviews";
        if (key.toLowerCase().includes("staff")) return "staff";
        if (key.toLowerCase().includes("shop")) return "shop";
        return "bookings";
    };

    const getSectionLabel = (section) =>
        translations[language]?.account?.notifications?.sections?.[section.toLowerCase()] || section;

    const getItemLabel = (key) => translations[language]?.account?.notifications?.items?.[key] || key;

    const sections = Object.keys(notificationPrefs).reduce((acc, key) => {
        const section = getSection(key);
        acc[section] = acc[section] || [];
        acc[section].push(key);
        return acc;
    }, {});

    return (
        <div className="sf-account-notifications-page">
            <h2 className="sf-account-notifications-title">{translations[language].account.notifications.title}</h2>

            {Object.entries(sections).map(([sectionTitle, keys]) => (
                <div key={sectionTitle} className="sf-notification-block">
                    <h3 className="sf-notification-block-title">{getSectionLabel(sectionTitle)}</h3>

                    {keys.map((key) => (
                        <div key={key} className="sf-notification-item">
                            <div className="sf-notification-item-title">{getItemLabel(key)}</div>

                            <div className="sf-notification-channels">
                                {/* EMAIL */}
                                <div className="sf-notification-channel" onClick={() => toggleChannel(key, "email")}>
                                    <span>{translations[language].account.notifications.emailLabel}</span>
                                    <div className={`sf-toggle ${notificationPrefs[key]?.email ? "on" : ""}`} />
                                </div>

                                {/* PUSH */}
                                <div className="sf-notification-channel" onClick={() => toggleChannel(key, "push")}>
                                    <span>{translations[language].account.notifications.pushLabel}</span>
                                    <div className={`sf-toggle ${notificationPrefs[key]?.push ? "on" : ""}`} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

export default AccountNotifications;
